import React, { useEffect, useState } from 'react';
import { useAuthorization } from 'framework/authorization';
import { RECRUITING_VACANCY_CLOSE_VACANCY, VACANCY_STATUS, useVacancyGroupService } from 'domain/shared/newVacancy';
import { ButtonWithConfirmation } from 'framework/ui/ui-builder';
import { useShowContext } from 'framework/show';
import { RECRUITING_CONTEXT } from '../../../RecruitingContext';
import { useVacancyContext } from 'domain/recruiting/newVacancy/VacancyContext';
import useVacancyPublicationService from 'domain/recruiting/newVacancy/show/publications/vacancyPublicationService';
import { Tooltip } from '@mui/material';

const CloseVacancyButton = ({ setGeneralContextData }: { setGeneralContextData: () => {} }) => {
  const { isGranted } = useAuthorization();
  const { closeVacancy, searchVacancy } = useVacancyGroupService();
  const { areVacanciesUpdated, arePublicationsUpdated, setAreVacanciesUpdated } = useVacancyContext();

  const { data } = useShowContext();
  const { id, status, areAllHired, vacancyGroupId } = data.getData();

  const [hasActivePublications, setHasActivePublications] = useState(true);
  const [hasOnlyOneActiveVacancy, setHasOnlyOneActiveVacancy] = useState(true);

  const { searchWithFilters } = useVacancyPublicationService();

  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const retrieveVacancies = async () => {
    const vacancies = await searchVacancy({
      filter: {
        'vacancyGroup.id': {
          eq: vacancyGroupId,
        },
        status: {
          eq: 'OPEN',
        },
      },
      pageSize: 1,
    } as any);

    setHasOnlyOneActiveVacancy(vacancies.totalElements === 1);
  };

  const retrievePublications = async () => {
    const publications = await searchWithFilters({
      filter: {
        'vacancyGroup.id': {
          eq: vacancyGroupId,
        },
        status: {
          eq: 'OPEN',
        },
      },
      pageSize: 1,
    } as any);

    setHasActivePublications(publications.totalElements !== 0);
  };

  useEffect(() => {
    (async () => {
      try {
        await retrieveVacancies();
        await retrievePublications();
      } finally {
        setIsInitialLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isInitialLoading) {
      (async () => {
        await retrieveVacancies();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areVacanciesUpdated]);

  useEffect(() => {
    if (!isInitialLoading) {
      (async () => {
        await retrievePublications();
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arePublicationsUpdated]);

  const disabledReason = {
    isDisabled: hasOnlyOneActiveVacancy && hasActivePublications,
    reason: (
      <ul>
        {hasOnlyOneActiveVacancy && hasActivePublications ? (
          <li>Can not close last active vacancy, as group contains active publications.</li>
        ) : (
          <></>
        )}
      </ul>
    ),
  };
  const closeButton = (
    <ButtonWithConfirmation
      displayMessage="Do you really want to close this vacancy, maybe some candidates not finished probation?"
      submitMethod={() => closeVacancy(id!)}
      afterSubmit={{
        successMessage: 'The Vacancy has been successfully closed.',
        errorMessage: "Can't close vacancy.",
        execute: () => {
          if (setGeneralContextData) {
            setGeneralContextData();
          }

          if (setAreVacanciesUpdated) {
            setAreVacanciesUpdated(!areVacanciesUpdated);
          }
        },
      }}
      permissionToCheck={null}
      disabled={disabledReason.isDisabled}
    >
      Close
    </ButtonWithConfirmation>
  );

  if (!(VACANCY_STATUS.EXPIRED === status || (VACANCY_STATUS.OPEN === status && areAllHired))) {
    return null;
  }

  return isGranted(RECRUITING_VACANCY_CLOSE_VACANCY, RECRUITING_CONTEXT) ? (
    <>
      {disabledReason.isDisabled && disabledReason.reason ? (
        <Tooltip title={disabledReason.reason}>
          <div>{closeButton}</div>
        </Tooltip>
      ) : (
        closeButton
      )}
    </>
  ) : null;
};

export default CloseVacancyButton;
